import { Navigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireStaff?: boolean;
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireStaff = false,
}: ProtectedRouteProps) {
  const { user, isLoading, logout } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to auth page if not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Block non-admin/staff users whose current-event registration is still pending approval.
  // Returning attendees retain status='approved' for previous events, so the middleware lets
  // them through — this gate enforces event-specific access on the frontend.
  const isPrivileged = user.roles?.includes('admin') || user.roles?.includes('staff');
  if (!isPrivileged && user.isPendingCurrentEvent) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md px-4">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center dark:bg-amber-900">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">Pending Admin Approval</h1>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            Your registration is awaiting admin review. You'll receive an email once you're approved.
          </p>
          <button
            onClick={logout}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm dark:text-blue-400 dark:hover:text-blue-300"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Check admin/staff role if required
  const hasAdminAccess = user.roles?.includes('admin') || user.roles?.includes('staff');
  const hasStaffAccess = user.roles?.includes('staff') || user.roles?.includes('admin');

  if (requireAdmin && !hasAdminAccess) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <a
            href="/"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (requireStaff && !hasStaffAccess) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <a
            href="/"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
